import React from 'react';
import { Avatar, Tooltip } from '@mui/material';
import classNames from 'classnames';
import classes from './ResourceCard.module.scss';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { isOwner } from 'js/lib/ACLUtils';
import TagsView from 'js/components/elements/tags/TagsView';
import { Stack } from 'new/elements/Stack';
import { bytesToString, formatNamedNumber, formatNumber } from 'js/lib/FormatUtils';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_FLOW_METRICS_REQUEST } from 'js/constants/actionTypes';
import UIUtils from 'js/lib/UIUtils';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { RuntimeStatusIndicator } from 'new/elements/DotsLoader/RuntimeStatusIndicator';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import useEntities from 'new/state/entities/repository/useEntities';
import { DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE } from 'js/constants/config';
import { getRuntimeStatus } from 'js/lib/RuntimeStatusUtils';

import { InfoContentDiscoveryMode } from 'js/components/pipelines/cards/InfoContentDiscoveryMode';

interface InfoContentProps {
  data: any;
  size: 'label' | 'x-small' | 'small' | 'normal';
  isDataFlowStatus?: boolean;
  tooltipTitle?: string;
  errorCount: number;
  recordCount: number;
  owner: any;
  isNodeDiscoveryEnabled?: boolean;
  recordVolume: number;
}

export const InfoContent = (props: InfoContentProps) => {
  const {
    data,
    isDataFlowStatus,
    size = 'normal',
    owner,
    tooltipTitle,
    errorCount,
    recordCount,
    isNodeDiscoveryEnabled,
    recordVolume,
  } = props;

  const loading = useIsLoading(GET_FLOW_METRICS_REQUEST);
  const ragFlowId = useRagFlowId();

  const recordLabel = UIUtils.toPluralFormat('record', recordCount, false);
  const errorLabel = UIUtils.toPluralFormat('error', errorCount, false);

  const getDataProcessedTooltipText = (includePrefix = false) => {
    let text = '';
    if (recordCount) {
      text = `${recordCount || 0} ${recordLabel}`;
    } else if (recordVolume) {
      text = bytesToString(recordVolume);
    }
    return includePrefix && text ? `Data processed: ${text}` : text;
  };

  const getDataProcessedDisplayText = () => {
    if (loading) return '...';
    if (recordCount) {
      return `${formatNamedNumber(recordCount)} rec.`;
    } else if (recordVolume) {
      return bytesToString(recordVolume);
    }
    return '';
  };

  // Temporary: remove with DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE feature flag
  const { data: sources } = useEntities('sources');
  const runtimeStatus = DETECTED_NEXSET_RUNTIME_STATUS_OVERRIDE
    ? getRuntimeStatus(data, sources)
    : data?.runtime_status;

  const parentArchivalStatus = data?.archival_status;

  if (isDataFlowStatus || isNodeDiscoveryEnabled) {
    return (
      <InfoContentDiscoveryMode
        data={data}
        owner={owner}
        errorCount={errorCount}
        recordCount={recordCount}
        recordVolume={recordVolume}
        omitProcessedDataInformation={!isDataFlowStatus}
        omitOwnerLabel // do not show owner label in preview mode.
        omitStatusBadge={false} // show status badge in discovery mode
        omitTags // do not show tags in preview mode.
        statusBadge={
          <PipelineStatusBadge
            size="small"
            hideInfo
            id={data.id}
            status={data.status as Status}
            ingestionMode={data?.ingestion_mode}
            runtimeStatus={runtimeStatus}
            parentArchivalStatus={parentArchivalStatus}
          />
        }
      />
    );
  }

  const ErrorCountView = (() => {
    // If we are in the context of rag flow, do not show errors or metrics
    if (ragFlowId) return null;
    if (errorCount && errorCount > 0) {
      return (
        <Typography className={classNames(classes.count, classes.errorCount)}>
          {formatNumber(errorCount)} {errorLabel}
        </Typography>
      );
    }
    return <Typography className={classes.count}></Typography>;
  })();

  return (
    <Stack gap="8px" className={classes.idContainer}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap="8px">
        <span data-testid={`info-content-id-${data.id}`}>
          <ChipWithCopy tooltipTitle={tooltipTitle} toCopy={data.id}>
            ID {data.id}
          </ChipWithCopy>
        </span>
        {/* If we are in the context of rag flow, do not show errors or metrics */}
        {size === 'normal' && !ragFlowId ? (
          <Tooltip title={getDataProcessedTooltipText(true)} arrow={true}>
            <Typography className={classes.count}>
              {/* some flow types (replication, custom) do not have record count, so we display record volume here */}
              {getDataProcessedDisplayText()}
            </Typography>
          </Tooltip>
        ) : null}
      </Stack>
      {size === 'normal' ? (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap="8px">
            <PipelineStatusBadge
              size="small"
              hideInfo
              id={data.id}
              status={data.status as Status}
              ingestionMode={data?.ingestion_mode}
              runtimeStatus={runtimeStatus}
              parentArchivalStatus={parentArchivalStatus}
            />
            {ErrorCountView}
          </Stack>
          <TagsView data={data} />
          {!isOwner(data) && owner && (
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              justifyContent="center"
              gap="6px"
            >
              <Typography className={classes.ownedByLabel}>Owned by</Typography>
              <Avatar className={classes.ownedByAvatar} sx={{ width: 16, height: 16 }}>
                {owner.name
                  .split(' ')
                  .slice(0, 2)
                  .map((group: string) => group[0]?.toUpperCase?.())
                  .join('')}
              </Avatar>
              <Typography className={classes.ownedByEmail}>{owner.email}</Typography>
            </Stack>
          )}
          {data.ingestion_mode === 'sampling' ? (
            <RuntimeStatusIndicator ingestionMode={data.ingestion_mode} />
          ) : null}
        </>
      ) : null}
    </Stack>
  );
};
